// src/components/inputs/FiniteSetInput.js
import React, { useEffect, useRef, useState } from 'react';
import { InlineMath } from 'react-katex';
import SetKeyboard from './SetKeyboard';

/* ---------- helpers ---------- */
// Rozpoznajemy tylko jawny symbol pustego zbioru.
const EMPTY_RE = /^(?:\u2205|\u00D8|\\?varnothing|\\?emptyset)$/i;

/**
 * Zwraca:
 *  - undefined -> brak odpowiedzi (puste pole / same nawiasy/whitespace),
 *  - []        -> zbior pusty (\u2205, \varnothing, \emptyset),
 *  - number[]  -> poprawnie sparsowany zbior liczb,
 *  - null      -> blad formatu.
 */
export function parseFiniteSetInput(raw) {
  if (raw == null) return undefined;
  const s = String(raw).trim().replace(/\u2212/g, '-');
  if (!s) return undefined;

  if (EMPTY_RE.test(s)) return [];

  // Same nawiasy/przecinki bez liczb traktujemy jako brak odpowiedzi.
  if (/^[\s{}\[\]\(\),;]*$/.test(s) && !/\d/.test(s)) return undefined;

  const cleaned = s
    .replace(/[{}\[\]\(\)]/g, ' ')
    .replace(/[;,]/g, ' ')
    .trim();

  if (!cleaned) return undefined;

  const parts = cleaned.split(/\s+/).filter(Boolean);
  const out = [];
  for (let i = 0; i < parts.length; i += 1) {
    const current = parts[i];
    const next = parts[i + 1];
    let token = current;
    if (/^[-+]?\d+$/.test(current) && /^\d+\/\d+$/.test(next || '')) {
      token = current + ' ' + next;
      i += 1;
    }
    const value = parseFiniteSetNumber(token);
    if (!Number.isFinite(value)) return null;
    if (!out.some((item) => Math.abs(item - value) < 1e-9)) out.push(value);
  }
  return out.sort((a, b) => a - b);
}

function parseFiniteSetNumber(raw) {
  const s = String(raw || '').trim().replace(',', '.');
  let m = s.match(/^([+-]?)\s*(\d+)\s+(\d+)\s*\/\s*(\d+)$/);
  if (m) {
    const [, sign, whole, n, d] = m;
    const den = Number(d);
    if (den === 0) return NaN;
    const value = Number(whole) + Number(n) / den;
    return sign === '-' ? -value : value;
  }
  m = s.match(/^([+-]?)\s*(\d+)\s*\/\s*(\d+)$/);
  if (m) {
    const [, sign, n, d] = m;
    const den = Number(d);
    if (den === 0) return NaN;
    const value = Number(n) / den;
    return sign === '-' ? -value : value;
  }
  if (/^[-+]?\d+(?:\.\d+)?$/.test(s)) return Number(s);
  return NaN;
}

export function finiteSetToLatex(list) {
  if (!Array.isArray(list)) return '';
  if (list.length === 0) return '\\varnothing';
  const body = list.map(formatSetNumberLatex).join(',\\,');
  return `\\{ ${body} \\}`;
}

function gcdFiniteSet(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const t = x % y;
    x = y;
    y = t;
  }
  return x || 1;
}

function formatSetNumberLatex(value) {
  const rounded = Math.round(value);
  if (Math.abs(value - rounded) < 1e-9) return String(rounded);
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  for (let d = 2; d <= 36; d += 1) {
    const n = Math.round(abs * d);
    if (Math.abs(abs - n / d) < 1e-9) {
      const g = gcdFiniteSet(n, d);
      return `${sign}\\dfrac{${n / g}}{${d / g}}`;
    }
  }
  return String(Number(value.toFixed(4))).replace('.', '{,}');
}

/* ---------- component ---------- */
export default function FiniteSetInput({
  value,
  onChange,
  disabled,
  placeholder = '{ -2, -1, 0, 1, 2 }',
  ariaLabel = 'zbi\u00f3r',
  showPreview = true,
}) {
  const [local, setLocal] = useState(
    typeof value === 'string' ? value : (value?.raw ?? '')
  );
  const [kbdOpen, setKbdOpen] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const parsed = parseFiniteSetInput(local);
    onChange && onChange({ raw: local, parsed });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);

  const parsed = parseFiniteSetInput(local);
  const latex = Array.isArray(parsed) ? finiteSetToLatex(parsed) : '';

  const setCaret = (pos) => {
    const el = inputRef.current; if (!el) return;
    el.focus(); el.setSelectionRange(pos, pos);
  };

  const insertAtCursor = (text) => {
    const el = inputRef.current; if (!el) return;
    const { selectionStart: s, selectionEnd: e } = el;
    const next = local.slice(0, s) + text + local.slice(e);
    setLocal(next);
    setTimeout(() => setCaret(s + text.length), 0);
  };

  const handleKbdAction = (type, payload) => {
    const el = inputRef.current; if (!el) return;
    const s = el.selectionStart, e = el.selectionEnd;

    if (type === 'insert') return insertAtCursor(String(payload ?? ''));
    if (type === 'backspace') {
      if (s !== e) {
        const next = local.slice(0, s) + local.slice(e);
        setLocal(next); setTimeout(() => setCaret(s), 0);
      } else if (s > 0) {
        const next = local.slice(0, s - 1) + local.slice(e);
        setLocal(next); setTimeout(() => setCaret(s - 1), 0);
      }
      return;
    }
    if (type === 'delete') {
      if (s !== e) {
        const next = local.slice(0, s) + local.slice(e);
        setLocal(next); setTimeout(() => setCaret(s), 0);
      } else if (e < local.length) {
        const next = local.slice(0, s) + local.slice(e + 1);
        setLocal(next); setTimeout(() => setCaret(s), 0);
      }
      return;
    }
    if (type === 'moveLeft')  { setTimeout(() => setCaret(Math.max(0, Math.min(s, e) - 1)), 0); return; }
    if (type === 'moveRight') { setTimeout(() => setCaret(Math.min(local.length, Math.max(s, e) + 1)), 0); return; }
    if (type === 'clear')     { setLocal(''); setTimeout(() => setCaret(0), 0); return; }
    if (type === 'enter')     { /* optional submit */ return; }
    if (type === 'toggle')    { setKbdOpen((v) => !v); return; }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Rzad 1: toggle klawiatury + input + szybkie znaki. */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
        <SetKeyboard
          render="toggle"
          targetRef={inputRef}
          open={kbdOpen}
          onOpenChange={setKbdOpen}
          onAction={handleKbdAction}
          compact
        />

        <input
          ref={inputRef}
          type="text"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          disabled={disabled}
          className="input-secondary"
          style={{ width: 340, maxWidth: '100%' }}
          placeholder={placeholder}
          aria-label={ariaLabel}
        />

        <button
          type="button"
          onClick={() => insertAtCursor('\u2205')}
          disabled={disabled}
          className="btn-secondary"
          title={"Wstaw \u2205 (zbi\u00f3r pusty)"}
        >{"\u2205"}</button>

        <button
          type="button"
          onClick={() => insertAtCursor('{')}
          disabled={disabled}
          className="btn-secondary"
          title={"Otw\u00f3rz klamr\u0119 {"}
        >{'{'}</button>

        <button
          type="button"
          onClick={() => insertAtCursor('}')}
          disabled={disabled}
          className="btn-secondary"
          title={"Zamknij klamr\u0119 }"}
        >{'}'}</button>
      </div>

      {/* Rzad 2: panel klawiatury. */}
      {kbdOpen && (
        <div style={{ width: '100%' }}>
          <SetKeyboard
            render="panel"
            targetRef={inputRef}
            open={kbdOpen}
            onOpenChange={setKbdOpen}
            onAction={handleKbdAction}
            compact
            panelIndent={36}
          />
        </div>
      )}

      {/* Podglad tylko gdy cos jest wpisane. */}
      {showPreview && local.trim() !== '' && (
        parsed === null ? (
          <div style={{ color: 'var(--danger, #f66)' }}>
            {"Podaj liczby lub u\u0142amki, np. { -2, 1/3, 4 }."}
          </div>
        ) : (
          Array.isArray(parsed) && (
            <div style={{ pointerEvents: 'none' }}>
              <strong>{"Podgl\u0105d:"}</strong>{' '}
              <InlineMath math={`\\displaystyle ${latex}`} />
            </div>
          )
        )
      )}
    </div>
  );
}
